import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { showSuccess, showError, showConfirmDanger } from '../services/swal';

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });

    // Helper to get currency symbol
    const currency = localStorage.getItem('currency_code') === 'EUR' ? '€' :
        localStorage.getItem('currency_code') === 'GBP' ? '£' :
            localStorage.getItem('currency_code') === 'PKR' ? 'Rs ' :
                localStorage.getItem('currency_code') === 'INR' ? '₹ ' : '$';

    // Financial Analytics Calculations
    const stats = useMemo(() => {
        const total = expenses.reduce((acc, curr) => acc + Number(curr.amount), 0);
        const today = new Date().toISOString().split('T')[0];
        const spendToday = expenses.filter(e => e.date === today).reduce((acc, curr) => acc + Number(curr.amount), 0);
        
        // Find highest category
        const categories = {};
        expenses.forEach(e => {
            categories[e.category] = (categories[e.category] || 0) + Number(e.amount);
        });
        const highestCategory = Object.keys(categories).reduce((a, b) => categories[a] > categories[b] ? a : b, 'N/A');

        return { total, spendToday, highestCategory, count: expenses.length };
    }, [expenses]);

    const fetchExpenses = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/expenses', { params: { search } });
            setExpenses(res.data.data);
        } catch (error) {
            console.error("Failed to fetch expenses", error);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await api.post('/expenses', formData);
            showSuccess('Expense Logged', 'Financial record has been committed to the ledger.');
            setShowModal(false);
            setFormData({ title: '', amount: '', category: '', date: new Date().toISOString().split('T')[0], notes: '' });
            fetchExpenses();
        } catch (error) {
            showError('Log Failed', 'Failed to save expense record.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await showConfirmDanger('Purge Record?', 'This financial entry will be permanently removed.');
        if (result.isConfirmed) {
            try {
                await api.delete(`/expenses/${id}`);
                fetchExpenses();
                showSuccess('Purged!', 'Expense record has been deleted.');
            } catch (error) {
                showError('Action Failed', 'Failed to delete the record.');
            }
        }
    };

    return (
        <Layout title="Expense Intelligence Terminal">
            <div className="expenses-workspace animate__animated animate__fadeIn">
                
                {/* Financial Insights Deck */}
                <div className="row g-4 mb-4">
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="insight-card p-4 rounded-4 shadow-sm border-0 h-100">
                            <div className="d-flex align-items-center gap-3">
                                <div className="insight-icon bg-danger bg-opacity-10 text-danger"><i className="bi bi-cash-stack fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Aggregate Spend</div>
                                    <h3 className="fw-extrabold mb-0">{currency}{stats.total.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="insight-card p-4 rounded-4 shadow-sm border-0 h-100 text-white" style={{background: 'linear-gradient(135deg, #FF4B5C 0%, #FF8066 100%)'}}>
                            <div className="d-flex align-items-center gap-3">
                                <div className="insight-icon bg-white bg-opacity-20"><i className="bi bi-calendar-check fs-4 text-warning" style={{color: '#ffc107 !important'}}></i></div>
                                <div>
                                    <div className="text-white text-opacity-75 small fw-bold text-uppercase ls-1">Spend Today</div>
                                    <h3 className="fw-extrabold mb-0">{currency}{stats.spendToday.toLocaleString(undefined, {minimumFractionDigits: 2})}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="insight-card p-4 rounded-4 shadow-sm border-0 h-100">
                            <div className="d-flex align-items-center gap-3">
                                <div className="insight-icon bg-warning bg-opacity-10 text-warning"><i className="bi bi-tags-fill fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Peak Category</div>
                                    <h3 className="fw-extrabold mb-0" style={{fontSize: '1.25rem'}}>{stats.highestCategory}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="insight-card p-4 rounded-4 shadow-sm border-0 h-100">
                            <div className="d-flex align-items-center gap-3">
                                <div className="insight-icon bg-info bg-opacity-10 text-info"><i className="bi bi-clipboard-data-fill fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Entry Count</div>
                                    <h3 className="fw-extrabold mb-0">{stats.count}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Ledger Card */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-transparent p-4 border-0">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                            <div>
                                <h5 className="fw-bold mb-0">Expense Ledger</h5>
                                <small className="text-muted">Audit and monitor all operational expenditures</small>
                            </div>
                            <div className="d-flex gap-2">
                                <div className="modern-search-box flex-grow-1">
                                    <i className="bi bi-search"></i>
                                    <input 
                                        type="text" 
                                        placeholder="Search by title or category..." 
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                                <button className="btn btn-danger rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2" onClick={() => setShowModal(true)}>
                                    <i className="bi bi-plus-circle"></i> <span className="d-none d-lg-inline">Log Expense</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 ledger-modern-table">
                                <thead>
                                    <tr>
                                        <th className="ps-4">Timestamp</th>
                                        <th>Expense Description</th>
                                        <th>Classification</th>
                                        <th>Audit Notes</th>
                                        <th className="text-end pe-4">Amount</th>
                                        <th className="text-center" style={{width:'80px'}}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-danger"></div></td></tr>
                                    ) : expenses.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5">
                                                <div className="py-4 opacity-50">
                                                    <i className="bi bi-receipt fs-1 text-muted"></i>
                                                    <p className="text-muted mt-2">The ledger is currently empty.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        expenses.map(expense => (
                                            <tr key={expense.id}>
                                                <td className="ps-4">
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-bold small">{new Date(expense.date).toLocaleDateString(undefined, {month:'short', day:'numeric', year:'numeric'})}</span>
                                                        <span className="text-muted x-small ls-1">COMMITTED</span>
                                                    </div>
                                                </td>
                                                <td><span className="fw-bold text-dark">{expense.title}</span></td>
                                                <td>
                                                    <span className={`badge rounded-pill px-3 py-1 x-small fw-bold text-uppercase ls-1 bg-opacity-10 ${
                                                        expense.category === 'Rent' ? 'bg-primary text-primary' :
                                                        expense.category === 'Utilities' ? 'bg-warning text-warning' :
                                                        expense.category === 'Salaries' ? 'bg-success text-success' :
                                                        'bg-secondary text-secondary'
                                                    }`}>
                                                        {expense.category}
                                                    </span>
                                                </td>
                                                <td><small className="text-muted text-truncate d-inline-block" style={{maxWidth: '180px'}}>{expense.notes || '-'}</small></td>
                                                <td className="text-end pe-4">
                                                    <span className="fw-extrabold text-danger">-{currency}{Number(expense.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                                                </td>
                                                <td className="text-center">
                                                    <button className="btn btn-icon-modern bg-danger bg-opacity-10 text-danger border-0" onClick={() => handleDelete(expense.id)} title="Delete Record">
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modern Modal */}
            {showModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }} onClick={() => setShowModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleSave} className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="modal-header border-0 p-4 pb-0">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="insight-icon bg-danger bg-opacity-10 text-danger"><i className="bi bi-lightning-charge-fill fs-4"></i></div>
                                    <div>
                                        <h5 className="modal-title fw-bold">Log Financial Outflow</h5>
                                        <small className="text-muted">Ensure precision in all expense reporting</small>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase ls-1">Expense Title <span className="text-danger">*</span></label>
                                    <input type="text" className="form-control custom-input" required placeholder="e.g. Server Maintenance, Office Supplies" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                                </div>
                                <div className="row g-4 mb-4">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Amount <span className="text-danger">*</span></label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0 fw-bold">{currency}</span>
                                            <input type="number" step="0.01" className="form-control custom-input border-start-0" required placeholder="0.00" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Date <span className="text-danger">*</span></label>
                                        <input type="date" className="form-control custom-input" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase ls-1">Category Classification</label>
                                    <select className="form-select custom-input" required value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                                        <option value="">Select Category...</option>
                                        <option value="Rent">🏢 Rent & Leasing</option>
                                        <option value="Utilities">⚡ Utilities & Energy</option>
                                        <option value="Salaries">👥 Payroll & Salaries</option>
                                        <option value="Inventory">📦 Inventory Procurement</option>
                                        <option value="Marketing">📣 Marketing & Adspend</option>
                                        <option value="Maintenance">🛠️ Maintenance & Repairs</option>
                                        <option value="Other">📝 Other Operational Costs</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label small fw-bold text-muted text-uppercase ls-1">Internal Audit Notes</label>
                                    <textarea className="form-control custom-input" rows="3" placeholder="Describe the purpose of this spend..." value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer border-0 p-4 pt-0">
                                <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn btn-danger rounded-pill px-5 fw-bold shadow-sm" disabled={isSaving}>
                                    {isSaving ? <><span className="spinner-border spinner-border-sm me-2"></span>Recording...</> : <><i className="bi bi-check-all me-2"></i>Commit Record</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .insight-card { background: var(--card-bg); transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid var(--border-color) !important; }
                .insight-card:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important; }
                .insight-icon { width: 52px; height: 52px; border-radius: 16px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                
                .modern-search-box { position: relative; min-width: 280px; }
                .modern-search-box i { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); color: var(--text-muted-color); }
                .modern-search-box input { width: 100%; height: 48px; padding: 0 15px 0 45px; border-radius: 50px; border: 1px solid var(--border-color); background: var(--bg-color); color: var(--text-color); outline: none; transition: all 0.3s ease; }
                .modern-search-box input:focus { border-color: var(--primary-color); box-shadow: 0 0 0 4px rgba(108, 93, 211, 0.1); }

                .ledger-modern-table thead th { background: var(--bg-color); color: var(--text-muted-color); font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 800; border: none; padding: 18px 12px; }
                .ledger-modern-table tbody td { padding: 18px 12px; border-bottom: 1px solid var(--border-color); }
                .ledger-modern-table tbody tr:last-child td { border-bottom: none; }
                
                .btn-icon-modern { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
                .btn-icon-modern:hover { transform: scale(1.1); }
                
                .custom-input { background: var(--bg-color); border: 1.5px solid var(--border-color); border-radius: 12px; padding: 12px 16px; font-weight: 500; transition: all 0.3s ease; color: var(--text-color); }
                .custom-input:focus { background: var(--card-bg); border-color: var(--primary-color); box-shadow: none; }
                .input-group-text { border: 1.5px solid var(--border-color); border-radius: 12px 0 0 12px !important; }
                .custom-input.border-start-0 { border-radius: 0 12px 12px 0 !important; }

                [data-bs-theme="dark"] .insight-card { background: #1A1A2E; }
                [data-bs-theme="dark"] .card { background: #1A1A2E; }
                [data-bs-theme="dark"] .modal-content { background: #1A1A2E; border: 1px solid rgba(255,255,255,0.05); }
                [data-bs-theme="dark"] .ledger-modern-table thead th { background: rgba(255,255,255,0.02); }
                [data-bs-theme="dark"] .text-dark { color: #fff !important; }
            `}</style>
        </Layout>
    );
};

export default Expenses;
